import { useState } from 'react';
import { CoinsIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BackLink,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  Field,
  LoadingState,
  PageEyebrow,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  TextArea,
  TextInput,
  formatCredits,
} from '@bcecloud/portal-ui';
import { getJson, sendJson, errorMessage } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  TierEditor,
  parseTiers,
  tiersAscend,
  type TierDraft,
} from '@/components/TierEditor';

/** A pricing template, as the API returns it. */
export interface PricingTemplate {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly tiers: readonly { readonly upTo: number; readonly credits: number }[];
  readonly incrementSize: number;
  readonly incrementCredits: number;
  readonly leadTimeDays: number;
  readonly travelSupplementPossible: boolean;
  readonly materialsSupplementPossible: boolean;
  /** How many catalog entries currently price from this template. */
  readonly usageCount: number;
}

/**
 * A compact reading of a template's breakpoints, e.g. "≤30 → 5 · ≤50 → 7".
 *
 * @param template - The template.
 * @returns The summary string.
 */
export function tiersSummary(template: Pick<PricingTemplate, 'tiers'>): string {
  return template.tiers.map((tier) => `≤${tier.upTo} → ${formatCredits(tier.credits)}`).join(' · ');
}

/** The template fields the search box matches. */
const TEMPLATE_SEARCH_FIELDS: ReadonlyArray<keyof PricingTemplate> = ['name', 'description'];

/**
 * BCE's reusable rate cards: named pricing structures — breakpoints plus an
 * increment rule and lead time — applied to programs from each client's
 * catalog editor. Editing a template reprices every program using it.
 *
 * @returns The pricing templates list page.
 */
export function PricingTemplatesPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<PricingTemplate | null>(null);
  const { search, setSearch } = useUrlSearch();

  const templates = useQuery({
    queryKey: ['pricing-templates'],
    queryFn: ({ signal }) => getJson<PricingTemplate[]>('/pricing-templates', undefined, signal),
  });
  const rows = filterRows(templates.data ?? [], search, TEMPLATE_SEARCH_FIELDS);

  const remove = useMutation({
    mutationFn: (id: string) => sendJson('delete', `/pricing-templates/${id}`),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['pricing-templates'] });
    },
  });

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="CLIENT PORTALS"
        title="Pricing templates"
        subtitle="Reusable pricing structures applied to programs from each client's catalog. Editing a template reprices every program that uses it."
        action={
          <Button variant="primary" onClick={() => navigate('/pricing-templates/new')}>
            New template
          </Button>
        }
      />

      <div className="mt-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput
            label="Search templates"
            placeholder="Name or description"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-sm"
          />
        </div>
        {templates.isPending ? (
          <LoadingState label="Loading templates…" />
        ) : templates.isError ? (
          <ErrorNotice title="Templates unavailable" message={errorMessage(templates.error)} />
        ) : templates.data.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<CoinsIcon aria-hidden className="size-6" />}
              title="No pricing templates yet"
              message="Create the first structure — a name, its credit breakpoints, and the increment rule for larger groups — and apply it to programs from any client's catalog."
              action={
                <Button variant="primary" onClick={() => navigate('/pricing-templates/new')}>
                  New template
                </Button>
              }
            />
          </Panel>
        ) : rows.length === 0 ? (
          <Panel>
            <EmptyState title="No templates match" message="Try a different search." />
          </Panel>
        ) : (
          <DataTable
            headers={['Template', 'Breakpoints', 'Beyond the last', 'Lead time', 'Used by', 'Actions']}
            visuallyHiddenHeaders={[5]}
          >
            {rows.map((template) => (
              <tr
                key={template.id}
                className="border-t border-portal-line align-middle text-portal-tiny"
              >
                <td className="px-4 py-2.5">
                  <button
                    type="button"
                    onClick={() => navigate(`/pricing-templates/${template.id}`)}
                    className="block cursor-pointer text-left text-portal-small font-bold underline-offset-2 hover:underline"
                  >
                    {template.name}
                  </button>
                  {template.description ? (
                    <span className="block max-w-[280px] truncate text-portal-micro text-portal-muted">
                      {template.description}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap tabular-nums">
                  {tiersSummary(template)}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-portal-muted">
                  +{formatCredits(template.incrementCredits)} per {template.incrementSize} people
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-portal-muted">
                  {template.leadTimeDays} days
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  {template.usageCount === 0 ? (
                    <Pill tone="neutral">Unused</Pill>
                  ) : (
                    <Pill tone="brand">
                      {template.usageCount} program{template.usageCount === 1 ? '' : 's'}
                    </Pill>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => navigate(`/pricing-templates/${template.id}`)}
                    className="cursor-pointer text-portal-micro font-semibold text-brand hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(template)}
                    className="ml-3 cursor-pointer text-portal-micro font-semibold text-rose-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>

      <ConfirmDialog
        open={deleting !== null}
        tone="danger"
        title={`Delete ${deleting?.name ?? 'this template'}?`}
        message={
          deleting !== null && deleting.usageCount > 0
            ? `It currently prices ${deleting.usageCount} program${deleting.usageCount === 1 ? '' : 's'} — move them to another template or ad-hoc pricing first.`
            : 'Programs are unaffected — nothing references it.'
        }
        confirmLabel="Delete template"
        confirmDisabled={deleting !== null && deleting.usageCount > 0}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
        }}
        onCancel={() => {
          if (!remove.isPending) setDeleting(null);
        }}
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
      />
    </section>
  );
}

/**
 * The create/edit page for one pricing template — a full page rather than a
 * drawer, because a rate card with several breakpoints plus its live preview
 * needs more room than a side panel gives it.
 *
 * @returns The template detail page.
 */
export function PricingTemplateDetailPage(): React.ReactElement {
  const { templateId } = useParams<{ templateId: string }>();
  const isNew = templateId === undefined || templateId === 'new';

  const templates = useQuery({
    queryKey: ['pricing-templates'],
    queryFn: ({ signal }) => getJson<PricingTemplate[]>('/pricing-templates', undefined, signal),
    enabled: !isNew,
  });

  if (!isNew && templates.isPending) return <LoadingState label="Loading template…" />;
  if (!isNew && templates.isError) {
    return <ErrorNotice title="Template unavailable" message={errorMessage(templates.error)} />;
  }

  const template = isNew
    ? null
    : (templates.data?.find((row) => row.id === templateId) ?? null);

  if (!isNew && template === null) {
    return (
      <ErrorNotice
        title="Template not found"
        message="It may have been deleted. Head back to the list."
      />
    );
  }

  return (
    <section className="animate-portal-rise">
      <BackLink to="/pricing-templates">All pricing templates</BackLink>

      <header className="mt-4">
        <PageEyebrow>PRICING TEMPLATE</PageEyebrow>
        <h1 className="text-portal-page tracking-[-1.7px]">
          {template ? template.name : 'New pricing template'}
        </h1>
        <p className="mt-1 text-sm text-admin-muted">
          {template
            ? `Priced onto ${template.usageCount} program${template.usageCount === 1 ? '' : 's'} — saving reprices every one of them immediately.`
            : 'A named structure you can apply to programs in any client’s catalog.'}
        </p>
      </header>

      <TemplateForm key={template?.id ?? 'new'} template={template} />
    </section>
  );
}

/**
 * Prices one group size against the drafted structure — the same arithmetic
 * the API's estimator applies, kept tiny here so the preview is honest.
 *
 * @param count - Group size.
 * @param tiers - Parsed, ascending breakpoints.
 * @param incrementSize - People per increment beyond the last breakpoint.
 * @param incrementCredits - Credits per increment.
 * @returns The credits, or null when the structure is incomplete.
 */
function previewPrice(
  count: number,
  tiers: readonly { upTo: number; credits: number }[],
  incrementSize: number,
  incrementCredits: number,
): number | null {
  const matched = tiers.find((tier) => count <= tier.upTo);
  if (matched) return matched.credits;
  const last = tiers[tiers.length - 1];
  if (!last || incrementSize <= 0) return null;
  return last.credits + Math.ceil((count - last.upTo) / incrementSize) * incrementCredits;
}

/**
 * The template form with its live preview: fields on the left, and on the
 * right the price ladder the draft produces — ranges per breakpoint and the
 * first few increment steps — so a mistyped tier is visible before it ships.
 *
 * @param props - The template being edited (null = creating).
 * @returns The form.
 */
function TemplateForm({
  template,
}: {
  readonly template: PricingTemplate | null;
}): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [tiers, setTiers] = useState<readonly TierDraft[]>(
    template && template.tiers.length > 0
      ? template.tiers.map((tier) => ({ upTo: String(tier.upTo), credits: String(tier.credits) }))
      : [{ upTo: '50', credits: '8' }],
  );
  const [incrementSize, setIncrementSize] = useState(String(template?.incrementSize ?? 10));
  const [incrementCredits, setIncrementCredits] = useState(
    String(template?.incrementCredits ?? 1),
  );
  const [leadTimeDays, setLeadTimeDays] = useState(String(template?.leadTimeDays ?? 21));

  const parsed = parseTiers(tiers);
  const ascending = tiersAscend(parsed);
  const valid =
    name.trim().length > 0 &&
    parsed.length > 0 &&
    parsed.length === tiers.length &&
    ascending &&
    Number(incrementSize) > 0 &&
    Number(incrementCredits) >= 0 &&
    Number(leadTimeDays) >= 0;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() === '' ? null : description.trim(),
        tiers: parsed,
        incrementSize: Number(incrementSize),
        incrementCredits: Number(incrementCredits),
        leadTimeDays: Number(leadTimeDays),
      };
      return template
        ? sendJson('patch', `/pricing-templates/${template.id}`, body)
        : sendJson('post', '/pricing-templates', body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pricing-templates'] });
      navigate('/pricing-templates');
    },
  });

  return (
    <form
      className="mt-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-[1.3fr_1fr]"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && !save.isPending) save.mutate();
      }}
    >
      <Panel className="space-y-5 p-6">
        <Field label="Name" htmlFor="template-name">
          <TextInput
            id="template-name"
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            placeholder="Standard team build"
          />
        </Field>

        <Field
          label="Description"
          htmlFor="template-description"
          hint="Optional — shown only on the template list."
        >
          <TextArea
            id="template-description"
            rows={2}
            maxLength={500}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <TierEditor tiers={tiers} onChange={setTiers} disabled={save.isPending} />

        <div>
          <span className="block text-portal-tiny font-bold">Beyond the last breakpoint</span>
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-portal-tiny text-portal-muted">Add</span>
            <TextInput
              aria-label="Credits per additional increment"
              type="number"
              min={0}
              value={incrementCredits}
              disabled={save.isPending}
              onChange={(event) => setIncrementCredits(event.target.value)}
              className="mt-0 w-[80px]"
            />
            <span className="text-portal-tiny text-portal-muted">credits per additional</span>
            <TextInput
              aria-label="People per increment"
              type="number"
              min={1}
              value={incrementSize}
              disabled={save.isPending}
              onChange={(event) => setIncrementSize(event.target.value)}
              className="mt-0 w-[80px]"
            />
            <span className="text-portal-tiny text-portal-muted">people</span>
          </div>
        </div>

        <Field
          label="Lead time (days)"
          htmlFor="template-lead"
          hint="How far ahead programs on this template must be booked."
        >
          <TextInput
            id="template-lead"
            type="number"
            min={0}
            max={365}
            value={leadTimeDays}
            onChange={(event) => setLeadTimeDays(event.target.value)}
            className="w-[120px]"
          />
        </Field>

        {parsed.length > 0 && !ascending ? (
          <ErrorNotice
            inline
            message="Breakpoints must increase — each “up to” must be larger than the one before it."
          />
        ) : null}
        {save.isError ? (
          <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} />
        ) : null}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!valid || save.isPending}>
            {save.isPending ? 'Saving…' : template ? 'Save template' : 'Create template'}
          </Button>
          <Button variant="secondary" type="button" onClick={() => navigate('/pricing-templates')}>
            Cancel
          </Button>
        </div>
      </Panel>

      <PricePreview
        tiers={parsed}
        ascending={ascending}
        incrementSize={Number(incrementSize)}
        incrementCredits={Number(incrementCredits)}
      />
    </form>
  );
}

/**
 * The price ladder a drafted structure produces: one row per breakpoint
 * range, then the first few increment steps beyond the last one.
 *
 * @param props - The parsed draft.
 * @returns The preview panel.
 */
function PricePreview({
  tiers,
  ascending,
  incrementSize,
  incrementCredits,
}: {
  readonly tiers: readonly { upTo: number; credits: number }[];
  readonly ascending: boolean;
  readonly incrementSize: number;
  readonly incrementCredits: number;
}): React.ReactElement {
  const rows: { readonly range: string; readonly credits: number }[] = [];

  if (ascending && tiers.length > 0) {
    let from = 1;
    for (const tier of tiers) {
      rows.push({ range: `${from}–${tier.upTo} people`, credits: tier.credits });
      from = tier.upTo + 1;
    }

    if (incrementSize > 0) {
      const last = tiers[tiers.length - 1] as { upTo: number; credits: number };
      for (let step = 1; step <= 3; step += 1) {
        const rangeStart = last.upTo + (step - 1) * incrementSize + 1;
        const rangeEnd = last.upTo + step * incrementSize;
        const price = previewPrice(rangeEnd, tiers, incrementSize, incrementCredits);
        if (price === null) break;
        rows.push({ range: `${rangeStart}–${rangeEnd} people`, credits: price });
      }
      rows.push({
        range: 'and so on',
        credits: -1,
      });
    }
  }

  return (
    <Panel className="p-6">
      <h2 className="text-portal-section font-bold tracking-[-0.4px]">What this charges</h2>
      <p className="mt-1 text-portal-tiny text-portal-muted">
        The price ladder this structure produces — updates as you type, so a mistyped breakpoint
        is visible before it reprices real programs.
      </p>

      {rows.length === 0 ? (
        <p className="mt-4 text-portal-small text-portal-muted">
          Add at least one valid breakpoint to see the ladder.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-portal-line">
          {rows.map((row) =>
            row.credits === -1 ? (
              <li key={row.range} className="py-2 text-portal-micro text-portal-muted">
                …then +{formatCredits(incrementCredits)} credit{incrementCredits === 1 ? '' : 's'} per additional{' '}
                {incrementSize} people
              </li>
            ) : (
              <li key={row.range} className="flex items-center justify-between py-2">
                <span className="text-portal-small">{row.range}</span>
                <span className="text-portal-small font-bold tabular-nums text-brand">
                  {formatCredits(row.credits)} credit{row.credits === 1 ? '' : 's'}
                </span>
              </li>
            ),
          )}
        </ul>
      )}
    </Panel>
  );
}
